/**
 * Time formatting, parsing, and option generation helpers.
 * Uses moment-timezone for all conversions to ensure timezone safety.
 */
import moment from 'moment-timezone';

// ---------------------------------------------------------------------------
// Time interval options (15-min increments, 12hr format)
// ---------------------------------------------------------------------------

export interface TimeOption {
  value: string;
  label: string;
}

export function generateTimeOptions(interval = 15): TimeOption[] {
  const options: TimeOption[] = [];
  for (let m = 0; m < 24 * 60; m += interval) {
    const h = Math.floor(m / 60);
    const min = m % 60;
    const period = h < 12 ? 'AM' : 'PM';
    const display = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const label = `${String(display).padStart(2, '0')}:${String(min).padStart(2, '0')} ${period}`;
    options.push({ value: label, label });
  }
  return options;
}

export const TIME_OPTIONS = generateTimeOptions();

// ---------------------------------------------------------------------------
// Parsing & formatting
// ---------------------------------------------------------------------------

/** Parse "HH:MM AM/PM" → { hours, minutes } in 24hr, or null if invalid. */
export function parse12hrTime(
  timeStr: string
): { hours: number; minutes: number } | null {
  const match = timeStr?.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return null;
  let h = parseInt(match[1], 10);
  const m = parseInt(match[2], 10);
  const period = match[3].toUpperCase();
  if (period === 'AM' && h === 12) h = 0;
  if (period === 'PM' && h !== 12) h += 12;
  return { hours: h, minutes: m };
}

/** Format an ISO string to "02:30 PM" using timezone. */
export function formatTime12(iso: string, timezone?: string): string {
  if (!iso) return '';
  const tz = timezone || moment.tz.guess();
  const m = moment.tz(iso, tz);
  if (!m.isValid()) return '';
  return m.format('hh:mm A');
}

/** Format an ISO string to a readable date using timezone. */
export function formatDateDisplay(iso: string, timezone?: string): string {
  if (!iso) return '';
  const tz = timezone || moment.tz.guess();
  const m = moment.tz(iso, tz);
  if (!m.isValid()) return '';
  return m.format('MM/DD/YYYY');
}

// ---------------------------------------------------------------------------
// Building ISO strings
// ---------------------------------------------------------------------------

/** Build an ISO string from a Date (for the date part) + a 12hr time string + timezone. */
export function buildIsoFromDateAndTime(
  date: Date | null,
  timeStr: string,
  timezone?: string
): string {
  if (!date || !timeStr) return '';
  const parsed = parse12hrTime(timeStr);
  if (!parsed) return '';
  const tz = timezone || moment.tz.guess();
  const dateStr = moment(date).format('YYYY-MM-DD');
  const timeFormatted = `${String(parsed.hours).padStart(2, '0')}:${String(parsed.minutes).padStart(2, '0')}`;
  const m = moment.tz(`${dateStr} ${timeFormatted}`, 'YYYY-MM-DD HH:mm', tz);
  return m.isValid() ? m.toISOString() : '';
}

/** Add `minutes` to an ISO string. Returns new ISO or ''. */
export function addMinutesToIso(iso: string, minutes: number): string {
  if (!iso) return '';
  const m = moment(iso);
  if (!m.isValid()) return '';
  return m.add(minutes, 'minutes').toISOString();
}

/** Diff in minutes between two ISO strings. */
export function diffMinutesIso(startIso: string, endIso: string): number {
  const s = moment(startIso);
  const e = moment(endIso);
  if (!s.isValid() || !e.isValid()) return 0;
  return Math.round(e.diff(s, 'minutes'));
}

/** Snap a duration to the nearest interval (default 15 min). */
export function snapToNearest(minutes: number, interval = 15): number {
  if (minutes <= 0) return interval;
  return Math.round(minutes / interval) * interval || interval;
}

/** Find the closest time option value for a given 12hr time string. */
export function findClosestTimeOption(
  timeStr: string,
  options: TimeOption[] = TIME_OPTIONS
): string {
  const parsed = parse12hrTime(timeStr);
  if (!parsed) return options[0]?.value ?? '';
  const targetMinutes = parsed.hours * 60 + parsed.minutes;

  let closest = options[0]?.value ?? '';
  let minDiff = Infinity;

  for (const opt of options) {
    const optParsed = parse12hrTime(opt.value);
    if (!optParsed) continue;
    const optMinutes = optParsed.hours * 60 + optParsed.minutes;
    const diff = Math.abs(optMinutes - targetMinutes);
    if (diff < minDiff) {
      minDiff = diff;
      closest = opt.value;
    }
  }
  return closest;
}

/**
 * Find the closest time option **at or after** a given 12hr time string —
 * i.e. ceiling to the next 15-min slot. Used for the End Time field so
 * a sub-15-min duration (e.g. start=10:00 + duration=6) does not snap
 * back to 10:00 (== startTime), which the End Time `<Select>` would then
 * auto-correct away — destroying the sub-15 duration the user picked.
 */
export function findCeilingTimeOption(
  timeStr: string,
  options: TimeOption[] = TIME_OPTIONS
): string {
  const parsed = parse12hrTime(timeStr);
  if (!parsed) return options[0]?.value ?? '';
  const targetMinutes = parsed.hours * 60 + parsed.minutes;

  let ceilingValue = '';
  let ceilingMinutes = Infinity;

  for (const opt of options) {
    const optParsed = parse12hrTime(opt.value);
    if (!optParsed) continue;
    const optMinutes = optParsed.hours * 60 + optParsed.minutes;
    if (optMinutes >= targetMinutes && optMinutes < ceilingMinutes) {
      ceilingMinutes = optMinutes;
      ceilingValue = opt.value;
    }
  }

  // No option at or after target — fall back to the latest option.
  return ceilingValue || options[options.length - 1]?.value || '';
}
